import React from "react";
import { Cpu, Brain, ShieldCheck, Workflow } from "lucide-react";

const ORBITS = [
  {
    id: "empire",
    label: "Empire Builder",
    desc: "Natural language → runnable Meta workflows.",
    icon: Workflow,
    angle: 0,
  },
  {
    id: "wingman",
    label: "Tam",
    desc: "Your productivity AI coach. Execution-first.",
    icon: Brain,
    angle: 120,
  },
  {
    id: "command",
    label: "Command Center",
    desc: "Admin build mode. Spin up partner pages on demand.",
    icon: ShieldCheck,
    angle: 240,
  },
];

const IntelligenceCore = () => {
  return (
    <section
      id="core"
      className="relative py-24 lg:py-32 border-y border-white/10 overflow-hidden"
      data-testid="intelligence-core-section"
    >
      {/* radial backdrop */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(0,240,255,0.10), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 border border-cyan-400/30 bg-cyan-400/5 text-cyan-300">
            <Cpu size={14} />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
              The Intelligence Core
            </span>
          </div>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]"
            data-testid="intelligence-core-headline"
          >
            One heart. <br />
            <span className="text-slate-500">Three engines.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-300 max-w-md">
            ClydeFlow's Intelligence Core orchestrates every move — building
            workflows, coaching operators, and running the back office in one
            beat. No tabs. No babysitting. Just execution.
          </p>

          <ul className="mt-8 space-y-3" data-testid="intelligence-core-list">
            {ORBITS.map((o, i) => {
              const Icon = o.icon;
              return (
                <li
                  key={o.id}
                  className="flex items-start gap-3 text-slate-200"
                  data-testid={`core-feature-${o.id}`}
                >
                  <div className="h-8 w-8 mt-1 flex items-center justify-center bg-[#0A192F] border border-cyan-400/30 rounded-sm">
                    <Icon size={14} className="text-cyan-300" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white">{o.label}</p>
                    <p className="text-sm text-slate-400">{o.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right: animated orbital visual */}
        <div className="lg:col-span-7 relative aspect-square max-w-xl mx-auto w-full">
          {/* concentric rings */}
          <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full" aria-hidden="true">
            <defs>
              <radialGradient id="core-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(0,240,255,0.6)" />
                <stop offset="60%" stopColor="rgba(0,240,255,0.05)" />
                <stop offset="100%" stopColor="rgba(0,240,255,0)" />
              </radialGradient>
              <linearGradient id="core-line" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#00F0FF" />
                <stop offset="100%" stopColor="#10B981" />
              </linearGradient>
            </defs>
            <circle cx="300" cy="300" r="260" fill="none" stroke="rgba(0,240,255,0.10)" />
            <circle cx="300" cy="300" r="200" fill="none" stroke="rgba(0,240,255,0.18)" />
            <circle cx="300" cy="300" r="140" fill="none" stroke="rgba(0,240,255,0.30)" strokeDasharray="4 6" />
            <circle cx="300" cy="300" r="100" fill="url(#core-glow)" />
            <circle cx="300" cy="300" r="60" fill="#0A192F" stroke="url(#core-line)" strokeWidth="2" />
            <g stroke="url(#core-line)" strokeWidth="3" strokeLinecap="round" fill="none">
              <path d="M270 290 H310" />
              <path d="M278 300 H322" />
              <path d="M288 310 H315" opacity="0.9" />
            </g>
          </svg>

          {/* orbiting chips */}
          {ORBITS.map((o, i) => {
            const Icon = o.icon;
            // Position chips along the 200-radius ring
            const r = 38; // percent radius (centered)
            const rad = (o.angle * Math.PI) / 180;
            const top = 50 - r * Math.cos(rad);
            const left = 50 + r * Math.sin(rad);
            return (
              <div
                key={o.id}
                data-testid={`core-orbit-${o.id}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2"
                style={{ top: `${top}%`, left: `${left}%` }}
              >
                <div
                  className="h-12 w-12 flex items-center justify-center bg-[#0A192F] border border-cyan-400/40 rounded-sm cf-glow"
                  style={{ animation: `cf-fadeup 0.8s ease ${0.2 + i * 0.15}s both` }}
                >
                  <Icon size={18} className="text-cyan-300" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-200 whitespace-nowrap">
                  {o.label}
                </p>
              </div>
            );
          })}

          {/* center label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
                Intelligence Core
              </p>
              <p className="font-heading font-black text-white text-xl mt-1">
                Online
              </p>
              <span className="mt-2 inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntelligenceCore;
